/**
 * Markdown渲染组件
 * 使用@uiw/react-md-editor的内置渲染功能，并添加Mermaid和KaTeX支持
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { useMarkdownRendererStyles } from '../../styles/components/markdown/MarkdownRenderer';
import { isUrlInWhitelist } from '../../utils/imageWhitelist';

// 导入Mermaid和KaTeX
import mermaid from 'mermaid';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// 全局初始化Mermaid配置
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis'
  },
  sequence: {
    useMaxWidth: true,
    diagramMarginX: 50,
    diagramMarginY: 10
  },
  gantt: {
    useMaxWidth: true
  },
  // 添加更多配置以提高兼容性
  pie: {
    useMaxWidth: true
  },
  journey: {
    useMaxWidth: true
  },
  class: {
    useMaxWidth: true
  },
  state: {
    useMaxWidth: true
  },
  er: {
    useMaxWidth: true
  },
  // 启用实验性功能
  experimental: {
    enableMultiplexer: true
  }
});

// 生成随机ID
const randomid = () => parseInt(String(Math.random() * 1e15), 10).toString(36);

// 防抖函数
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// 安全的哈希函数，支持Unicode字符
const generateHash = (str) => {
  if (!str) return '';
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 转换为32位整数
  }
  
  // 转换为正数并转为16进制字符串
  return Math.abs(hash).toString(16).slice(0, 8);
};

// Mermaid图表渲染组件
const MermaidRenderer = ({ children = [], className, ...props }) => {
  const demoid = useRef(`dome${randomid()}`);
  const [container, setContainer] = useState(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState('');
  const isMermaid = className && /^language-mermaid/.test(className.toLowerCase());
  
  // 获取代码内容 - 确保是字符串类型
  const getCodeContent = () => {
    if (!children) return "";
    
    // 如果children是数组
    if (Array.isArray(children)) {
      // 遍历所有children，收集所有文本内容
      const allTexts = [];
      
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (typeof child === 'string') {
          allTexts.push(child);
        } else if (typeof child === 'number') {
          allTexts.push(String(child));
        } else if (child && typeof child === 'object' && child.props) {
          // 递归获取React元素的文本内容
          const extractText = (element) => {
            if (typeof element === 'string') {
              return element;
            }
            if (typeof element === 'number') {
              return String(element);
            }
            if (Array.isArray(element)) {
              return element.map(extractText).join('');
            }
            if (element && typeof element === 'object' && element.props) {
              return extractText(element.props.children);
            }
            return '';
          };
          const childText = extractText(child.props.children);
          if (childText) {
            allTexts.push(childText);
          }
        }
      }
      
      return allTexts.join('');
    }
    
    // 如果children是字符串
    if (typeof children === 'string') {
      return children;
    }
    
    // 如果children是React元素
    if (children && typeof children === 'object' && children.props) {
      // 递归获取所有文本内容
      const extractText = (element) => {
        if (typeof element === 'string') {
          return element;
        }
        if (typeof element === 'number') {
          return String(element);
        }
        if (Array.isArray(element)) {
          return element.map(extractText).join('');
        }
        if (element && typeof element === 'object' && element.props) {
          return extractText(element.props.children);
        }
        return '';
      };
      return extractText(children.props.children);
    }
    
    return "";
  };
  
  const code = getCodeContent();
  
  // 使用防抖减少频繁渲染
  const debouncedCode = useDebounce(code, 300);

  useEffect(() => {
    if (container && isMermaid && demoid.current && debouncedCode) {
      // 确保代码是有效的字符串
      if (typeof debouncedCode !== 'string' || !debouncedCode.trim()) {
        console.warn('Mermaid代码不是有效的字符串:', debouncedCode);
        return;
      }
      
      // 检查是否已经渲染过相同的内容
      const codeHash = generateHash(debouncedCode);
      const existingHash = container.getAttribute('data-code-hash');
      
      if (existingHash === codeHash && svgContent) {
        // 如果代码没有变化且已经渲染过，直接返回
        return;
      }
      
      try {
        // 预处理Mermaid代码，确保第一行包含图表类型声明
        let processedCode = debouncedCode.trim();
        
        // 如果代码不以图表类型开头，尝试自动检测
        if (!processedCode.match(/^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|gitgraph|C4Context|C4Container|C4Component|C4Code|C4Dynamic|C4Deployment|mindmap|timeline|zenuml|sankey|quadrantChart|requirement|gitgraph|user-journey|gitgraph|pie|quadrantChart|requirement|user-journey|sankey|timeline|zenuml)/i)) {
          // 尝试从代码中提取图表类型
          const lines = processedCode.split('\n').filter(line => line.trim());
          if (lines.length > 0) {
            const firstLine = lines[0].trim();
            // 如果第一行看起来像图表内容，添加默认的graph类型
            if (firstLine.includes('-->') || firstLine.includes('---') || firstLine.includes('==>')) {
              processedCode = `graph TD\n${processedCode}`;
            } else if (firstLine.includes('[') && firstLine.includes(']')) {
              processedCode = `graph TD\n${processedCode}`;
            }
          }
        }
        

        
        // 渲染Mermaid图表
        mermaid.render(demoid.current, processedCode).then(({ svg, bindFunctions }) => {
          // 检查容器是否仍然存在
          if (container && container.parentNode) {
            try {
              container.innerHTML = svg;
              container.setAttribute('data-code-hash', codeHash);
              setSvgContent(svg);
              setError('');
              
              if (bindFunctions) {
                bindFunctions(container);
              }
            } catch (domError) {
              console.error('DOM操作错误:', domError);
            }
          }
        }).catch((error) => {
          console.error('Mermaid渲染错误:', error);
          setError(error.message);
          if (container && container.parentNode) {
            try {
              container.innerHTML = `<div style="color: red;">Mermaid渲染失败: ${error.message}</div>`;
            } catch (domError) {
              console.error('DOM操作错误:', domError);
            }
          }
        });
      } catch (err) {
        console.error('Mermaid初始化错误:', err);
        setError(err.message);
        if (container && container.parentNode) {
          try {
            container.innerHTML = `<div style="color: red;">Mermaid初始化失败: ${err.message}</div>`;
          } catch (domError) {
            console.error('DOM操作错误:', domError);
          }
        }
      }
    }
  }, [container, isMermaid, debouncedCode, demoid, svgContent]);

  const refElement = useCallback((node) => {
    if (node !== null && node !== container) {
      setContainer(node);
    } else if (node === null && container !== null) {
      setContainer(null);
    }
  }, [container]);

  // 清理函数
  useEffect(() => {
    return () => {
      if (container && container.parentNode) {
        try {
          container.innerHTML = '';
        } catch (error) {
          console.warn('清理容器时出错:', error);
        }
      }
    };
  }, [container]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      setSvgContent('');
      setError('');
      setContainer(null);
    };
  }, []);

  if (isMermaid) {
    return (
      <div className="my-4">
        <code id={demoid.current} className="hidden" />
        <div 
          ref={refElement} 
          data-name="mermaid"
          className="block h-75 border border-gray-300 rounded-lg p-5 bg-white shadow-sm overflow-auto"
        />

      </div>
    );
  }
  
  return <code className={className} {...props}>{children}</code>;
};

// KaTeX数学公式渲染组件
const MathRenderer = ({ code }) => {
  const [rendered, setRendered] = useState('');
  const [error, setError] = useState('');
  const [isRendering, setIsRendering] = useState(false);

  useEffect(() => {
    // 确保code是有效的字符串
    if (!code || typeof code !== 'string' || !code.trim()) {
      return;
    }
    
    if (!isRendering) {
      setIsRendering(true);
      
      try {
        // 渲染数学公式
        const html = katex.renderToString(code.trim(), {
          throwOnError: false,
          errorColor: '#cc0000',
          displayMode: true,
          output: 'html'
        });
        setRendered(html);
        setError('');
      } catch (err) {
        setError(`数学公式渲染失败: ${err.message}`);
        console.error('KaTeX渲染错误:', err);
      } finally {
        setIsRendering(false);
      }
    }
  }, [code, isRendering]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-3 my-2 text-red-600">
        <strong>数学公式渲染失败:</strong> {error}
      </div>
    );
  }

  if (isRendering) {
    return (
      <div className="bg-gray-100 border border-gray-300 rounded p-3 my-2 text-center text-gray-600">
        正在渲染数学公式...
      </div>
    );
  }

  return (
    <div 
      className="my-4 text-center overflow-auto p-5 bg-white border border-gray-300 rounded-lg shadow-sm h-25 flex items-center justify-center"
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
};

const MarkdownRenderer = ({ 
  content, 
  className = '',
  showLineNumbers = false,
  enableMath = true,
  enableHighlight = true
}) => {
  const { styles } = useMarkdownRendererStyles();

  // 计算固定高度，与DocumentModal中的计算逻辑保持一致
  const calculateFixedHeight = () => {
    const windowHeight = window.innerHeight;
    // 计算可用高度：窗口高度 - Modal的top值(20px) - 标题栏高度(约80px) - footer高度(约60px) - 额外padding和margin(120px)
    const availableHeight = windowHeight - 20 - 80 - 60 - 120;
    // 设置最小高度为450px，最大高度为可用高度的80%
    return Math.max(450, Math.floor(availableHeight * 0.9));
  };

  const fixedHeight = calculateFixedHeight();

  return (
    <div className={`${styles.rendererContainer} ${className}`}>
              <MDEditor.Markdown
          source={content}
          className="p-4 bg-transparent border-none overflow-auto"
          style={{
            height: fixedHeight
          }}
        components={{
          code: ({ inline, children, className, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const isMermaid = className && /^language-mermaid/.test(className.toLowerCase());
            const isMath = className && /^language-math/.test(className.toLowerCase());
            
            // 处理Mermaid图表
            if (!inline && isMermaid) {
              return <MermaidRenderer children={children} className={className} {...props} />;
            }
            
                         // 处理KaTeX数学公式
             if (!inline && isMath) {
               // 提取数学公式代码
               const getMathCode = () => {
                 if (!children) return "";
                 
                 if (Array.isArray(children)) {
                   // 遍历所有children，收集所有文本内容
                   const allTexts = [];
                   
                   for (let i = 0; i < children.length; i++) {
                     const child = children[i];
                     if (typeof child === 'string') {
                       allTexts.push(child);
                     } else if (typeof child === 'number') {
                       allTexts.push(String(child));
                     } else if (child && typeof child === 'object' && child.props) {
                       // 递归获取React元素的文本内容
                       const extractText = (element) => {
                         if (typeof element === 'string') return element;
                         if (typeof element === 'number') return String(element);
                         if (Array.isArray(element)) return element.map(extractText).join('');
                         if (element && typeof element === 'object' && element.props) {
                           return extractText(element.props.children);
                         }
                         return '';
                       };
                       const childText = extractText(child.props.children);
                       if (childText) {
                         allTexts.push(childText);
                       }
                     }
                   }
                   
                   return allTexts.join('');
                 }
                 
                 if (typeof children === 'string') return children;
                 
                 if (children && typeof children === 'object' && children.props) {
                   // 递归获取所有文本内容
                   const extractText = (element) => {
                     if (typeof element === 'string') return element;
                     if (typeof element === 'number') return String(element);
                     if (Array.isArray(element)) return element.map(extractText).join('');
                     if (element && typeof element === 'object' && element.props) {
                       return extractText(element.props.children);
                     }
                     return '';
                   };
                   return extractText(children.props.children);
                 }
                 
                 return "";
               };
               
               return <MathRenderer code={getMathCode()} />;
             }
            
            // 处理其他代码块
            if (!inline && match) {
              return (
                <pre className={className}>
                  <code className={`language-${match[1]}`} {...props}>
                    {children}
                  </code>
                </pre>
              );
            }
            
            // 处理内联代码
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          ul: ({ children, ...props }) => (
            <ul className="list-disc pl-8 my-4" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="list-decimal pl-8 my-4" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="my-1 list-item" {...props}>
              {children}
            </li>
          ),
                     // 添加图片白名单验证
           img: ({ src, alt, ...props }) => {
             // 验证图片URL是否在白名单中
             if (!isUrlInWhitelist(src)) {
               return (
                 <div className="bg-red-50 border border-red-200 rounded p-2 mb-2 flex items-center text-red-600 text-sm">
                   <svg className="mr-2 w-4 h-4 fill-red-500" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                     <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                   </svg>
                   不允许上传外部图片
                 </div>
               );
             }
            
            return (
              <img
                src={src}
                alt={alt}
                className="max-w-full h-auto rounded-lg mb-3 max-h-96"
                {...props}
              />
            );
          }
        }}
      />
    </div>
  );
};

export default MarkdownRenderer;

